import { loginAction, updateAccountDetail } from '@avantia/azure-ad-auth';
import { isDefined } from '@avantia/form-validation';
import { useMsal } from '@azure/msal-react';
import { ReactElement, useEffect, useLayoutEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { getAnswersAction } from '../actions/getAnswersAction';
import {
  HpClaimsGetQuestionSetActionProps,
  HpGetQuestionSetActionResponse,
  getQuestionSetAction
} from '../actions/getQuestionSetAction';
import { HpClaimsDebugFlags, logDebug, logWarning } from '../clientLogging';
import { HpClaimsClientConfiguration } from '../config/configurationTypes';
import { getAuthenticatedUserAction } from '../engineClient/actions/getAuthenticatedUserAction'; // Import index instead.
import { getFeatureToggleStateAction } from '../engineClient/actions/getFeatureToggleStateAction';
import { getQuestionSetsAction } from '../engineClient/actions/getQuestionSetsAction';
import { HpGetQuestionSetsActionResponse } from '../engineClient/actions/getQuestionSetsActionTypes';
import { viewportChangedAction } from '../engineClient/actions/viewportChangedAction';
import {
  HpViewportFunc,
  checkViewportWithDelay,
  maybeAlignControlsVertically
} from '../engineClient/components/pageComponentUtilities';
import { createQuestionSetPage } from '../engineClient/components/questionSetForm/questionSetPage';
import {
  HpRenderSideBarProps,
  HpSideBarRendererFunc
} from '../engineClient/components/questionSetForm/questionSetPageTypes';
import { registerConfigurationAction } from '../engineClient/config/registerClientConfig';
import { getAnswersFromAnswerMap } from '../engineClient/tools/clientAndServerUtilities';
import { HpPostScriptErrorFunction } from '../engineClient/tools/postScriptError';
import { HpClientAnswer, HpClientQuestionSetState, HpUserInterfaceState } from '../engineClient/types';
import { HpFeatureToggleState } from '../engineClient/types/featureToggle';
import { getIsMockDataModeEnabled, isAzureAdSession, setAgentCookie } from '../handlers/handlerUtilities';
import { HpPoliciesState } from '../types/policyTypes';
import { sectionIds } from '../types/questionConstants';
import { HpClaimEnquiryResultState, HpGlobalState, HpUserState } from '../types/stateTypes';
import ClaimEnquiryResultPage from './claimConfirmationPage';
import ErrorPage from './errorPage';
import { Footer } from './footer';
import { Header } from './header';
import { MockDataModeIndicator } from './mockDataModeIndicator';
import { createClaimsPageComponentFunctions, maybeTriggerClaimAlertEventsWithDelay } from './pageComponentUtilities';
import { questionSetPageProps } from './questionSetForm/questionSetPageProps';
import { LoadingWidget } from './widgets/loadingWidget';

declare const process: any;

let websiteBaseUrl: string;
let websiteEndpoint: string;

registerConfigurationAction((config) => {
  const { environment: env, websiteBaseUrl: baseUrl } = config as HpClaimsClientConfiguration;
  const isDeveloperEnv = env === 'developer' || env === 'unit-tests';
  websiteBaseUrl = isDeveloperEnv ? 'http://localhost:3000' : baseUrl;
  ({ websiteEndpoint } = config);
});

export type HpAppProps = HpAppStateProps & HpAppDispatchProps;

interface HpClaimsCreateSideBarProps extends HpRenderSideBarProps {
  createSideBarEngineFunc: HpSideBarRendererFunc;
}

const createSideBar = (props: HpClaimsCreateSideBarProps): ReactElement | null => {
  const { activeSectionId, createSideBarEngineFunc } = props;
  const { coverAlert } = sectionIds;
  if (activeSectionId === coverAlert) {
    return null;
  }

  return createSideBarEngineFunc(props);
};

const QuestionSetPage = createQuestionSetPage({
  ...questionSetPageProps,
  createSideBar: (props: HpRenderSideBarProps) =>
    createSideBar({ ...props, createSideBarEngineFunc: questionSetPageProps.createSideBar })
});

export interface HpAppStateProps {
  featureToggles: HpFeatureToggleState;
  userInterface: HpUserInterfaceState;
  questionSet: HpClientQuestionSetState;
  policies: HpPoliciesState;
  claimEnquiryResult: HpClaimEnquiryResultState;
  user: HpUserState;
}

function App({ getQuestionSet, userInterface, viewportChanged, questionSet, user }: HpAppProps): JSX.Element {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(true);
  if (!isLoggedIn) {
    if (!isAzureAdSession()) {
      // TODO - move to redux
      setIsLoggedIn(true);
      setIsLoginLoading(false);
      createClaimsPageComponentFunctions();
    }
  }

  const { viewport } = userInterface;
  const { answers, productId, questionSetId, questions, answersId } = questionSet;
  const dispatch = useDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0] || undefined;

  useEffect(() => {
    window.addEventListener('resize', () => checkViewportWithDelay({ viewport, viewportChanged }));
  }, [viewport]);

  useEffect(() => {
    if (isLoggedIn || inProgress !== 'none') {
      return;
    }

    if (!account) {
      logDebug(`In progress? ${inProgress}`, HpClaimsDebugFlags.None);
      loginAction({ instance, websiteBaseUrl, type: 'redirect', acquireType: 'popup' })(dispatch);
    } else {
      if (!user || user.name !== account.name) {
        updateAccountDetail({ account, dispatch, instance, websiteBaseUrl, acquireType: 'popup' })
          .then(() => {
            setAgentCookie(account.name as string);
            setIsLoggedIn(true);
            setIsLoginLoading(false);
            createClaimsPageComponentFunctions();
          })
          .catch((reason) => {
            logWarning(
              { message: 'An error occurred while updating the user account', reason },
              HpClaimsDebugFlags.None
            );
          });
      }
    }
  });

  useEffect(() => {
    if (user && user.sessionExpired && account && inProgress === 'none' && isAzureAdSession()) {
      loginAction({ instance, websiteBaseUrl, type: 'redirect', acquireType: 'popup' })(dispatch);
    }
  }, [user, account, inProgress]);

  useLayoutEffect(() => {
    // TODO - Dynamic lookup items will not always be correctly aligned if reprocessAlignedItems is set to false.
    maybeAlignControlsVertically({ reprocessAlignedItems: true });
    maybeTriggerClaimAlertEventsWithDelay(questions);
  });

  const qsetIdAnswer: HpClientAnswer = answers.questionSetId;

  useEffect(() => {
    if (
      qsetIdAnswer &&
      !!qsetIdAnswer.customer &&
      qsetIdAnswer.customer !== questionSetId &&
      isDefined(qsetIdAnswer.customer)
    ) {
      const questionSetId = qsetIdAnswer.customer as string;
      const { emailAddress } = getAnswersFromAnswerMap({
        answers,
        keys: ['emailAddress'],
        productId
      });
      getQuestionSet({
        productId,
        questionSetId,
        metadata: { productId, questionSetId, version: '' },
        queryParams: { emailAddress },
        needs: '',
        keepAnswers: true,
        isNewJourney: false,
        answersId
      });
    }
  }, [qsetIdAnswer]);

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <div className="App">
        <Header />
        <main className="container hp-body">
          {isLoggedIn ? (
            <Switch>
              <Route exact path={`${websiteEndpoint}/`} component={QuestionSetPage} />
              <Route path={`${websiteEndpoint}/confirmation`} component={ClaimEnquiryResultPage} />
              <Route path={`${websiteEndpoint}/*`} component={ErrorPage} />
            </Switch>
          ) : (
            <Switch>
              {isLoginLoading ? (
                <Route
                  path={`${websiteEndpoint}/*`}
                  render={(props) => <LoadingWidget {...props} viewport={viewport} />}
                />
              ) : (
                <Route path={`${websiteEndpoint}/*`} component={ErrorPage} />
              )}
            </Switch>
          )}
        </main>
        <Switch>
          <Route component={Footer} />
        </Switch>
        {getIsMockDataModeEnabled() && <MockDataModeIndicator />}
      </div>
    </Router>
  );
}

interface HpAppDispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  getFeatureToggleState: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  getAuthenticatedUser: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  getAnswers: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  viewportChanged: HpViewportFunc;
  getQuestionSet: (props: HpClaimsGetQuestionSetActionProps) => HpGetQuestionSetActionResponse;
  getQuestionSets: () => HpGetQuestionSetsActionResponse;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: HpGlobalState) => {
  const { userInterface, featureToggles, questionSet, policies, claimEnquiryResult, user } = state;
  return { userInterface, featureToggles, questionSet, policies, claimEnquiryResult, user };
};

const mapDispatchToProps = {
  // By passing the mapDispatchToProps as an object redux will wrap the properties with dispatch.
  getFeatureToggleState: getFeatureToggleStateAction,
  getAuthenticatedUser: getAuthenticatedUserAction,
  getAnswers: getAnswersAction,
  getQuestionSet: getQuestionSetAction,
  getQuestionSets: getQuestionSetsAction,
  viewportChanged: viewportChangedAction
};

export default connect(mapStateToProps, mapDispatchToProps)(App as any);

declare global {
  interface Window {
    postScriptError: HpPostScriptErrorFunction;
  }
}
